import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { membershipHasPermission } from '../../auth/permissions';
import type { CreateInvitationResponse } from '../../types';
import { darkTheme, FONT } from '../../theme/tokens';
import { InviteMemberForm } from './InviteMemberForm';
import { PendingInvitesList } from './PendingInvitesList';
import { TeamMembersList } from './TeamMembersList';

const t = darkTheme;

// The tenant-facing team surface. Every active member sees the roster (SC-3227);
// managing it is gated per affordance on a Permission (not a role string), so a
// future per-org permission override needs no change here:
//   - title edit       → EDIT_MEMBER_TITLE (Admin and up)
//   - invite + pending → INVITE_MEMBER (Owner, SC-2795)
// The staff provisioning console (SC-2784) is a separate, EKOM_ADMIN-gated app
// section.
export function TeamTab() {
  const { membership } = useAuth();
  const canInvite = membershipHasPermission(membership, 'invite_member');
  const canEditTitle = membershipHasPermission(membership, 'edit_member_title');
  // Owned here (not in the form) so revoking the just-created invite clears its
  // hand-carry token callout — a revoked token is dead, don't keep offering it.
  const [lastCreated, setLastCreated] = useState<CreateInvitationResponse | null>(null);

  return (
    <div style={{ padding: '28px 32px', maxWidth: 860, fontFamily: FONT }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: t.text, margin: '0 0 4px' }}>Team</h1>
      <p style={{ fontSize: 13, color: t.text3, margin: '0 0 24px' }}>
        People in your organization.
      </p>

      <TeamMembersList currentMembershipId={membership?.id ?? null} canEditTitle={canEditTitle} />

      {canInvite && membership ? (
        // Invitations are not members: a separate band below the roster, so
        // someone invited never reads as someone who joined.
        <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 24 }}>
          <InviteMemberForm
            inviterRole={membership.role}
            lastCreated={lastCreated}
            onCreated={setLastCreated}
          />
          <PendingInvitesList
            onRevoked={(id) =>
              setLastCreated((cur) => (cur?.invitation.id === id ? null : cur))
            }
          />
        </div>
      ) : (
        <p style={{ fontSize: 12.5, color: t.text3, margin: 0 }}>
          Only your organization&rsquo;s owner can invite teammates.
        </p>
      )}
    </div>
  );
}
